import React, { useState, useEffect, useMemo, useRef } from "react";
import "../Suivi.css";
import "../../ToDo/ToDo.css";
import { IoIosSearch } from "react-icons/io";
import { httpClient } from '../../../HttpClient';
import { useEtatGlobal } from "../../EtatGlobal";
import { missionCommencee, missionActive, contactBloque } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";
import { normalizeText } from "../../../utils/text.js";

import { showStatusToast } from "../../../components/ui/Toast";
import { CiFilter } from "react-icons/ci";
import {
  ExternalLink,
  X,
  Save,
  Loader2,
  Check,
  CheckCircle2,
  Circle,
  ChevronDown,
  User,
  Mail,
  MapPin,
  CreditCard,
  FileText,
  Shield,
  AlertCircle,
} from "lucide-react";

const AVAILABLE_YEARS = [2024, 2025, 2026, 2027];

// Les 4 vrais statuts (SuiviIR.statuts, saisis via l'overlay d'édition) —
// pastille + badge + teinte de ligne complète, dans l'ordre du cycle de vie
// du dossier (pas commencé → bloqué côté client → en cours → terminé).
const STATUT_ORDER = ["À faire", "En attente client", "En cours", "Finalisé"];
const STATUT_META = {
  "À faire": {
    dot: "bg-red-500",
    hex: "#B43A3A",
    pill: "bg-red-50 text-red-700 border-red-200",
    row: "bg-red-50/50 hover:bg-red-50",
    stat: "bg-red-50 text-red-700",
  },
  "En attente client": {
    dot: "bg-orange-400",
    hex: "#B45309",
    pill: "bg-orange-50 text-orange-700 border-orange-200",
    row: "bg-orange-50/50 hover:bg-orange-50",
    stat: "bg-orange-50 text-orange-700",
  },
  "En cours": {
    dot: "bg-amber-400",
    hex: "#8b6a0f",
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    row: "bg-amber-50/50 hover:bg-amber-50",
    stat: "bg-amber-50 text-amber-700",
  },
  Finalisé: {
    dot: "bg-emerald-500",
    hex: "#2A6D3C",
    pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    row: "bg-emerald-50/50 hover:bg-emerald-50",
    stat: "bg-emerald-50 text-emerald-700",
  },
};
const STATUT_DEFAUT = {
  dot: "bg-slate-300",
  hex: "#94A3B8",
  pill: "bg-slate-100 text-slate-500 border-slate-200",
  row: "",
  stat: "bg-slate-100 text-slate-500",
};

// Accord client (SuiviIR.accordClient, valeurs réelles "Oui"/"Non"/"En
// attente") — simple pastille de couleur, pas de badge texte (peu de place).
const ACCORD_DOT = {
  Oui: "bg-emerald-500",
  Non: "bg-red-400",
  "En attente": "bg-amber-400",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d)) return "-";
  return d.toLocaleDateString("fr-FR");
};

const isOverdue = (ir) => {
  if (!ir?.dueDate) return false;
  if (ir.validation) return false;
  return new Date(ir.dueDate) < new Date();
};

function initiales(u) {
  return `${u.name?.[0] || ""}${u.lastName?.[0] || ""}`.toUpperCase();
}

// Cellule "Collaborateur" du tableau — avatar + nom si affecté, sinon icône
// + "Affecter...". Clic ouvre une liste avec recherche (même principe que le
// champ "Collaborateur assigné" de l'overlay complet).
function CollabPicker({ user, allUsers, isOpen, onToggle, onSelect, search, onSearchChange }) {
  const filtered = allUsers.filter((u) =>
    normalizeText(`${u.name} ${u.lastName}`).includes(normalizeText(search)),
  );
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={onToggle}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs transition-colors ${
          user
            ? "border-gray-200 bg-white hover:border-[#840040] text-gray-700"
            : "border-dashed border-gray-300 text-gray-400 hover:border-[#840040] hover:text-[#840040]"
        }`}
      >
        {user ? (
          <span className="w-5 h-5 rounded-full bg-[#840040] text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">
            {initiales(user)}
          </span>
        ) : (
          <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <User className="w-2.5 h-2.5 text-gray-400" />
          </span>
        )}
        <span className="font-medium whitespace-nowrap">
          {user ? `${user.name} ${user.lastName}`.trim() : "Affecter..."}
        </span>
        <ChevronDown className="w-3 h-3 flex-shrink-0" />
      </button>
      {isOpen && (
        <div
          className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-100 z-20 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={onSearchChange}
              placeholder="Rechercher un collaborateur..."
              className="w-full h-7 px-2 rounded-md border border-gray-200 text-xs focus:outline-none focus:border-[#840040]"
            />
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            <button
              onClick={() => onSelect(null)}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${
                !user ? "font-semibold text-[#840040]" : "text-gray-600"
              }`}
            >
              Non affecté
            </button>
            <div className="h-px bg-gray-100 my-1" />
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-300 italic">Aucun résultat</p>
            ) : (
              filtered.map((u) => (
                <button
                  key={u.id}
                  onClick={() => onSelect(u.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${
                    user?.id === u.id ? "font-semibold text-[#840040]" : "text-gray-600"
                  }`}
                >
                  <span className="w-5 h-5 rounded-full bg-[#840040]/10 text-[#840040] text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                    {initiales(u)}
                  </span>
                  <span className="truncate">
                    {u.lastName} {u.name}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const QUESTIONS_LABELS = [
  "Rencontre et attestation d'identité",
  "Personne politiquement exposée (PPE)",
  "Doutes sur l'intégrité et la réputation",
  "Gel des avoirs",
];

function ContactDetailOverlay({ contact, onClose }) {
  const isPPE = contact.ppe === true;
  const isIdentiteVerifiee = contact.identiteVerifiee === true;
  const initials =
    `${(contact.prenoms || "").charAt(0)}${(contact.nom || "").charAt(
      0
    )}`.toUpperCase() || "?";

  const qReponses = [
    {
      label: QUESTIONS_LABELS[0],
      reponse: contact.question1Reponse,
      commentaire: contact.question1Commentaire,
    },
    {
      label: QUESTIONS_LABELS[1],
      reponse: contact.question2Reponse,
      commentaire: contact.question2Commentaire,
    },
    {
      label: QUESTIONS_LABELS[2],
      reponse: contact.question3Reponse,
      commentaire: contact.question3Commentaire,
    },
    {
      label: QUESTIONS_LABELS[3],
      reponse: contact.question4Reponse,
      commentaire: contact.question4Commentaire,
    },
  ];
  const hasQuestionnaire = qReponses.some((q) => q.reponse);

  return (
    <div className="overlayAC" onClick={onClose}>
      <div
        className="inoverlayLead"
        style={{ width: "90%", maxWidth: "860px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            zIndex: 10000,
          }}
        >
          <span className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition flex items-center justify-center">
            <X className="w-4 h-4" />
          </span>
        </button>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            padding: "0 5%",
            boxSizing: "border-box",
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center shrink-0">
              <span className="text-base font-bold text-white">{initials}</span>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">
                {contact.nom} {contact.prenoms}
              </div>
              <div className="flex items-center gap-2 mt-1">
                {isPPE && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-md border border-amber-400 bg-amber-50 text-amber-700">
                    PPE
                  </span>
                )}
                {isIdentiteVerifiee && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-md border border-blue-300 bg-blue-50 text-blue-700 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Identité vérifiée
                  </span>
                )}
                {contact.autresMissions && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-md border border-emerald-400 bg-emerald-50 text-emerald-700">
                    IR
                  </span>
                )}
                {contact.clients?.length > 0 && (
                  <span className="text-xs text-slate-500">
                    {contact.clients.length} dossier
                    {contact.clients.length > 1 ? "s" : ""} lié
                    {contact.clients.length > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Body — two columns */}
          <div
            className="grid gap-5"
            style={{
              gridTemplateColumns:
                contact.clients?.length > 0 ? "1fr 260px" : "1fr",
            }}
          >
            <div className="space-y-4 pb-2">
              {/* Identité */}
              {(contact.dateNaissance ||
                contact.villeNaissance ||
                contact.paysNaissance ||
                contact.nationalite) && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-[#9B1B5D]" />
                    Identité
                  </h3>
                  <div className="space-y-2">
                    {contact.dateNaissance && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">
                          Date de naissance
                        </span>
                        <span className="font-medium text-slate-900">
                          {contact.dateNaissance}
                        </span>
                      </div>
                    )}
                    {contact.villeNaissance && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">
                          Ville de naissance
                        </span>
                        <span className="font-medium text-slate-900">
                          {contact.villeNaissance}
                        </span>
                      </div>
                    )}
                    {contact.paysNaissance && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">
                          Pays de naissance
                        </span>
                        <span className="font-medium text-slate-900">
                          {contact.paysNaissance}
                        </span>
                      </div>
                    )}
                    {contact.nationalite && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Nationalité</span>
                        <span className="font-medium text-slate-900">
                          {contact.nationalite}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Adresse */}
              {(contact.rue ||
                contact.codePostal ||
                contact.villeAdresse ||
                contact.paysAdresse) && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#9B1B5D]" />
                    Adresse de résidence
                  </h3>
                  <div className="space-y-2">
                    {contact.rue && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Rue</span>
                        <span className="font-medium text-slate-900">
                          {contact.rue}
                        </span>
                      </div>
                    )}
                    {contact.codePostal && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Code postal</span>
                        <span className="font-medium text-slate-900">
                          {contact.codePostal}
                        </span>
                      </div>
                    )}
                    {contact.villeAdresse && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Ville</span>
                        <span className="font-medium text-slate-900">
                          {contact.villeAdresse}
                        </span>
                      </div>
                    )}
                    {contact.paysAdresse && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Pays</span>
                        <span className="font-medium text-slate-900">
                          {contact.paysAdresse}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact info */}
              {(contact.email || contact.telephone) && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#9B1B5D]" />
                    Contact
                  </h3>
                  <div className="space-y-2">
                    {contact.email && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Email</span>
                        <span className="font-medium text-slate-900">
                          {contact.email}
                        </span>
                      </div>
                    )}
                    {contact.telephone && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Téléphone</span>
                        <span className="font-medium text-slate-900">
                          {contact.telephone}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pièce d'identité */}
              {contact.pieceIdentite && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#9B1B5D]" />
                    Pièce d'identité
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Document</span>
                      <span className="font-medium text-slate-900">
                        {contact.pieceIdentite}
                      </span>
                    </div>
                    {contact.dateEmission && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Date d'émission</span>
                        <span className="font-medium text-slate-900">
                          {contact.dateEmission}
                        </span>
                      </div>
                    )}
                    {contact.dateExpiration && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">
                          Date d'expiration
                        </span>
                        <span className="font-medium text-slate-900">
                          {contact.dateExpiration}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Questionnaire */}
              {hasQuestionnaire && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#9B1B5D]" />
                    Questionnaire associé / BE
                  </h3>
                  <div className="space-y-2.5">
                    {qReponses
                      .filter((q) => q.reponse)
                      .map((q, i) => (
                        <div key={i} className="flex gap-3 text-xs">
                          <span className="text-slate-500 shrink-0 w-44 leading-snug">
                            {q.label}
                          </span>
                          <div>
                            <span
                              className={`font-semibold ${
                                q.reponse === "Oui"
                                  ? "text-emerald-600"
                                  : q.reponse === "Non"
                                  ? "text-rose-600"
                                  : "text-slate-500"
                              }`}
                            >
                              {q.reponse}
                            </span>
                            {q.commentaire && (
                              <p className="text-slate-500 mt-0.5 italic">
                                {q.commentaire}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Commentaire global */}
              {contact.commentaireGlobal && (
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#9B1B5D]" />
                    Commentaire global
                  </h3>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    {contact.commentaireGlobal}
                  </p>
                </div>
              )}
            </div>

            {/* Right — dossiers liés */}
            {contact.clients?.length > 0 && (
              <div className="flex flex-col gap-3">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#9B1B5D]" />
                    Dossiers liés
                  </h3>
                  <div className="flex flex-col gap-2">
                    {contact.clients.map((c) => (
                      <div
                        key={c.id}
                        className="flex items-center gap-2.5 p-2.5 bg-[#9B1B5D]/5 rounded-lg border border-[#9B1B5D]/10"
                      >
                        <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#9B1B5D] to-[#7A1548] flex items-center justify-center shrink-0">
                          <FileText className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-slate-900 truncate block">
                            {c.nom?.toUpperCase() || "—"}
                          </span>
                          {c.missionIR === "oui" && (
                            <span className="text-[10px] text-emerald-600 font-medium">
                              Mission IR
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SuiviIR({ suivi, loading, refetch }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState(2025);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showDetailOverlay, setShowDetailOverlay] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [detailContact, setDetailContact] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [animatingId, setAnimatingId] = useState(null);

  // Réaffectation rapide "Collaborateur" directement depuis le tableau —
  // avatar/"Affecter..." cliquable, ouvre une liste avec recherche.
  const [openCollabMenuId, setOpenCollabMenuId] = useState(null);
  const [collabSearch, setCollabSearch] = useState("");

  useEffect(() => {
    if (openCollabMenuId == null) return;
    const onDoc = () => {
      setOpenCollabMenuId(null);
      setCollabSearch("");
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [openCollabMenuId]);

  // "Répartition par état" (voir JuridiquePonctuelle.js) : cliquer sur une
  // carte de statut filtre le tableau sur ce statut ; un second clic (ou
  // "Tout afficher") retire le filtre.
  const [activeStatutFilter, setActiveStatutFilter] = useState(null);

  // Bouton "Filtrer" à droite de l'année — dimensions non couvertes par les
  // cartes de statut (Collaborateur / Logiciel / Accord client).
  const FILTRE_VIDE = { collaborateur: [], logiciel: [], accordClient: [] };
  const [filtres, setFiltres] = useState(FILTRE_VIDE);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    if (!filterOpen) return;
    const onDoc = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [filterOpen]);

  const toggleFiltre = (groupe, valeur) =>
    setFiltres((f) => ({
      ...f,
      [groupe]: f[groupe].includes(valeur)
        ? f[groupe].filter((v) => v !== valeur)
        : [...f[groupe], valeur],
    }));
  const filtreCount = Object.values(filtres).reduce((n, a) => n + a.length, 0);

  // Utilisateurs déjà chargés au démarrage dans EtatGlobal (fetchAll) : plus
  // de requête /api/users ni de squelette à chaque ouverture de l'écran.
  const { users } = useEtatGlobal();
  const allUsers = users || [];
  const [searchUser, setSearchUser] = useState("");
  const [userSuggestions, setUserSuggestions] = useState([]);

  const [formIR, setFormIR] = useState({
    logiciel: "",
    statuts: "",
    accordClient: "",
    validation: false,
    userId: null,
    dueDate: null,
  });

  const getUserNameById = (userId) => {
    if (!userId) return "-";
    const user = allUsers.find((u) => u.id === userId);
    if (!user) return "-";
    return `${user.lastName || ""} ${user.name || ""}`.trim();
  };

  const handleSearchUser = (e) => {
    const q = e.target.value;
    setSearchUser(q);
    if (q.length < 2) {
      setUserSuggestions([]);
      return;
    }
    const filtered = allUsers.filter((user) =>
      normalizeText(`${user.name} ${user.lastName}`).includes(normalizeText(q))
    );
    setUserSuggestions(filtered);
  };

  const selectUser = (user) => {
    setFormIR((prev) => ({ ...prev, userId: user.id }));
    setSearchUser(`${user.lastName || ""} ${user.name || ""}`.trim());
    setUserSuggestions([]);
  };

  const getIRForYear = (annuaire) => {
    return (
      annuaire.iRbyAnnuaires?.find((ir) => ir.annee === selectedYear) || {}
    );
  };

  const handleOpenOverlay = (annuaire) => {
    setSelectedClient(annuaire);
    const ir = getIRForYear(annuaire);
    setFormIR({
      logiciel: ir.logiciel || "",
      statuts: ir.statuts || "",
      accordClient: ir.accordClient || "",
      validation: ir.validation || false,
      userId: ir.userId || null,
      dueDate: ir.dueDate || null,
    });
    if (ir.userId) {
      const user = allUsers.find((u) => u.id === ir.userId);
      if (user)
        setSearchUser(`${user.lastName || ""} ${user.name || ""}`.trim());
      else setSearchUser("");
    } else {
      setSearchUser("");
    }
    setShowOverlay(true);
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
    setSelectedClient(null);
    setSearchUser("");
    setUserSuggestions([]);
  };

  const handleSubmit = async () => {
    if (!selectedClient) return;
    setIsSubmitting(true);
    const clientId = selectedClient.id;
    const isValidated = formIR.validation === true;
    const payload = {
      userId: formIR.userId || null,
      logiciel: formIR.logiciel,
      statuts: formIR.statuts,
      accordClient: formIR.accordClient,
      validation: formIR.validation,
      annee: selectedYear,
      dueDate: formIR.dueDate || null,
    };
    handleCloseOverlay();
    if (isValidated) {
      const row = document.querySelector(`[data-client-id="${clientId}"]`);
      if (row) row.classList.add("todo-row-validating");
      setAnimatingId(clientId);
      setTimeout(() => setAnimatingId(null), 1300);
    }
    const toast = showStatusToast({
      status: "loading",
      titre: "Enregistrement...",
      message: "Mise à jour du dossier IR en cours.",
    });
    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/suiviIR/${clientId}`,
        payload
      );
      toast.update({
        status: "success",
        titre: "Dossier mis à jour",
        message: `${selectedClient.nom} ${selectedClient.prenoms} — modifications enregistrées.`,
      });
    } catch (err) {
      console.error("Erreur update SuiviIR", err);
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "La mise à jour n'a pas pu être enregistrée.",
      });
    } finally {
      setIsSubmitting(false);
      if (refetch) refetch();
    }
  };

  // Réaffectation rapide depuis le tableau — même endpoint que l'overlay
  // complet, ne modifie que userId (les autres champs de l'année sont repris
  // tels quels).
  const handleQuickReassign = async (annuaire, newUserId) => {
    setOpenCollabMenuId(null);
    setCollabSearch("");
    const ir = getIRForYear(annuaire);
    const toast = showStatusToast({
      status: "loading",
      titre: "Réaffectation...",
      message: "Mise à jour du collaborateur en cours.",
    });
    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/suiviIR/${annuaire.id}`,
        {
          userId: newUserId,
          logiciel: ir.logiciel || "",
          statuts: ir.statuts || "",
          accordClient: ir.accordClient || "",
          validation: ir.validation || false,
          annee: selectedYear,
          dueDate: ir.dueDate || null,
        }
      );
      toast.update({
        status: "success",
        titre: "Collaborateur mis à jour",
        message: `${annuaire.nom} ${annuaire.prenoms} — réaffectation enregistrée.`,
      });
    } catch (err) {
      console.error("Erreur réaffectation IR", err);
      toast.update({
        status: "error",
        titre: "Erreur",
        message: "La réaffectation a échoué.",
      });
    } finally {
      if (refetch) refetch();
    }
  };

  // Filtré par année (mission active) mais PAS par la recherche — sert de
  // base stable aux KPI et aux cartes collaborateur, indépendamment de ce qui
  // est tapé dans la recherche (qui ne restreint que le tableau).
  const suiviAnnee = useMemo(
    () =>
      (suivi || []).filter((annuaire) => {
        if (annuaire.clients?.length > 0) {
          return annuaire.clients.some(
            (c) => missionCommencee(c, selectedYear) && missionActive(c, selectedYear),
          );
        }
        return true;
      }),
    [suivi, selectedYear],
  );

  // Les contacts dont tous les dossiers sont bloqués (litige) restent dans le
  // tableau, grisés, mais ne comptent dans aucun KPI ni carte collaborateur.
  const suiviKpi = useMemo(
    () => suiviAnnee.filter((a) => !contactBloque(a)),
    [suiviAnnee],
  );

  const filteredSuivi = suiviAnnee.filter((annuaire) => {
    const fullName = `${annuaire.nom || ""} ${annuaire.prenoms || ""}`.toLowerCase();
    if (!fullName.includes(searchTerm.toLowerCase())) return false;
    const ir = getIRForYear(annuaire);
    if (activeStatutFilter && ir.statuts !== activeStatutFilter) return false;
    if (filtres.collaborateur.length && !filtres.collaborateur.includes(ir.userId || null))
      return false;
    if (filtres.logiciel.length && !filtres.logiciel.includes(ir.logiciel || ""))
      return false;
    if (
      filtres.accordClient.length &&
      !filtres.accordClient.includes(ir.accordClient || "")
    )
      return false;
    return true;
  });

  const logicielsDisponibles = useMemo(
    () =>
      Array.from(
        new Set(suiviAnnee.map((a) => getIRForYear(a).logiciel).filter(Boolean)),
      ).sort(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [suiviAnnee, selectedYear],
  );

  // KPI par statut — total et pourcentage sur l'ensemble des dossiers IR de
  // l'année sélectionnée (avant recherche).
  const totalAnnee = suiviKpi.length;
  const kpis = STATUT_ORDER.map((s) => ({
    statut: s,
    count: suiviKpi.filter((a) => getIRForYear(a).statuts === s).length,
  }));

  // Cartes par collaborateur — uniquement ceux ayant au moins un dossier IR
  // affecté cette année (pas la liste complète des utilisateurs).
  const collabCards = useMemo(() => {
    const parCollab = new Map();
    suiviKpi.forEach((a) => {
      const ir = getIRForYear(a);
      if (!ir.userId) return;
      if (!parCollab.has(ir.userId)) parCollab.set(ir.userId, []);
      parCollab.get(ir.userId).push(ir);
    });
    return Array.from(parCollab.entries())
      .map(([userId, irs]) => {
        const user = allUsers.find((u) => u.id === userId);
        if (!user) return null;
        return {
          user,
          total: irs.length,
          finalise: irs.filter((ir) => ir.statuts === "Finalisé").length,
          enCours: irs.filter(
            (ir) => ir.statuts === "En cours" || ir.statuts === "En attente client",
          ).length,
          aFaire: irs.filter((ir) => ir.statuts === "À faire").length,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.total - a.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suiviKpi, allUsers]);
  const nonAffectes = suiviKpi.filter((a) => !getIRForYear(a).userId).length;

  if (loading) {
    return (
      <div>
        <div className="mb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="h-9 w-72 rounded-xl bg-slate-200 animate-pulse" />
            <div className="h-9 w-24 rounded-lg bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />
          ))}
        </div>
        <div className="w-full overflow-x-auto">
          <table
            className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
            style={{ fontSize: "0.72rem" }}
          >
            <thead>
              <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
                {["Dossier", "Collaborateur", "Logiciel", "Statut", "Accord", "Validation", "Échéance", ""].map(
                  (col, i) => (
                    <th key={i} className="px-3 py-2.5 text-left">
                      <div
                        className="h-3 rounded bg-[#ffffff33] animate-pulse"
                        style={{ width: col ? "80%" : "40px" }}
                      />
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="bg-white">
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-40 mb-1" />
                    <div className="h-2 rounded bg-slate-100 animate-pulse w-16" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-6 rounded-lg bg-slate-200 animate-pulse w-28" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-16" />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="h-5 w-24 rounded-full bg-slate-200 animate-pulse" />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="h-2.5 w-2.5 rounded-full bg-slate-200 animate-pulse mx-auto" />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="h-4 w-4 rounded-full bg-slate-200 animate-pulse mx-auto" />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-12 mx-auto" />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="h-6 rounded bg-slate-200 animate-pulse w-16 ml-auto" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="relative flex-1 max-w-sm">
            <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filtrer par nom ou prénom..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 w-full rounded-xl bg-white border border-gray-200 text-sm focus:outline-none focus:border-gray-300"
            />
          </div>
          <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="h-9 pl-3 pr-7 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 appearance-none cursor-pointer focus:outline-none focus:border-gray-300"
            >
              {AVAILABLE_YEARS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <ChevronDown
              size={13}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setFilterOpen((v) => !v)}
              className={`flex items-center gap-2 h-9 px-3 rounded-md font-semibold text-sm border transition-colors ${
                filtreCount > 0
                  ? "border-[#6e1a36] text-[#6e1a36] bg-[#6e1a36]/5"
                  : "border-slate-300 bg-white text-black hover:bg-slate-50"
              }`}
            >
              <CiFilter className="w-3.5 h-3.5" />
              Filtrer
              {filtreCount > 0 && (
                <span className="bg-[#6e1a36] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                  {filtreCount}
                </span>
              )}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 p-4 z-50">
                {[
                  {
                    key: "collaborateur",
                    label: "Collaborateur",
                    options: [
                      { value: null, label: "Non affecté" },
                      ...collabCards.map((c) => ({
                        value: c.user.id,
                        label: `${c.user.name} ${c.user.lastName}`,
                      })),
                    ],
                  },
                  {
                    key: "logiciel",
                    label: "Logiciel",
                    options: logicielsDisponibles.map((l) => ({ value: l, label: l })),
                  },
                  {
                    key: "accordClient",
                    label: "Accord client",
                    options: [
                      { value: "Oui", label: "Oui" },
                      { value: "Non", label: "Non" },
                      { value: "En attente", label: "En attente" },
                    ],
                  },
                ].map((groupe) => (
                  <div key={groupe.key} className="mb-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">
                      {groupe.label}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {groupe.options.length === 0 ? (
                        <p className="text-[11px] text-gray-300 italic">Aucune valeur</p>
                      ) : (
                        groupe.options.map((opt) => {
                          const actif = filtres[groupe.key].includes(opt.value);
                          return (
                            <button
                              key={opt.label}
                              onClick={() => toggleFiltre(groupe.key, opt.value)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] transition-colors ${
                                actif
                                  ? "bg-[#6e1a36] border-[#6e1a36] text-white"
                                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                            >
                              {actif && <Check className="w-2.5 h-2.5" />}
                              {opt.label}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100 mt-1">
                  <button
                    disabled={!filtreCount}
                    onClick={() => setFiltres(FILTRE_VIDE)}
                    className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Réinitialiser
                  </button>
                  <button
                    onClick={() => setFilterOpen(false)}
                    className="px-4 py-1.5 rounded-lg bg-[#6e1a36] hover:bg-[#5a1429] text-white text-xs font-semibold transition-colors"
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>

      {/* Répartition par état — mêmes cartes cliquables que JuridiquePonctuelle.js,
          sans carte englobante ni texte d'en-tête. */}
      <div className="mb-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpis.map(({ statut, count }) => {
            const meta = STATUT_META[statut] || STATUT_DEFAUT;
            const isActive = activeStatutFilter === statut;
            return (
              <button
                key={statut}
                onClick={() => setActiveStatutFilter(isActive ? null : statut)}
                className="rounded-lg px-3 pt-2.5 pb-3 relative overflow-hidden text-left transition-all"
                style={{
                  backgroundColor: "#faf7f4",
                  border: isActive ? `2px solid ${meta.hex}` : "1px solid #e8e0d8",
                  boxShadow: isActive ? `0 0 0 2px ${meta.hex}22` : "none",
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: meta.hex }}
                    />
                    <span className="text-xs text-slate-600 leading-tight truncate">
                      {statut}
                    </span>
                  </div>
                  <span className="text-base font-bold ml-2 flex-shrink-0 text-slate-900">
                    {count}
                    <span className="text-xs font-medium text-slate-400"> / {totalAnnee}</span>
                  </span>
                </div>
                <div
                  className="h-0.5 w-6 rounded-full"
                  style={{ backgroundColor: meta.hex }}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Cartes collaborateurs */}
      {(collabCards.length > 0 || nonAffectes > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          {collabCards.map(({ user, total, finalise, enCours, aFaire }) => (
            <div key={user.id} className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-[#840040] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {initiales(user)}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-800 m-0 truncate">
                    {user.name} {user.lastName}
                  </p>
                  <p className="text-[10px] text-gray-400 m-0">
                    {total} Dossier{total > 1 ? "s" : ""} IR
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className="flex-1 text-center text-[11px] font-bold rounded-md py-1 bg-amber-50 text-amber-700"
                  title="En cours / en attente client"
                >
                  {enCours}
                </span>
                <span
                  className="flex-1 text-center text-[11px] font-bold rounded-md py-1 bg-emerald-50 text-emerald-700"
                  title="Finalisé"
                >
                  {finalise}
                </span>
                <span
                  className="flex-1 text-center text-[11px] font-bold rounded-md py-1 bg-red-50 text-red-700"
                  title="À faire"
                >
                  {aFaire}
                </span>
              </div>
            </div>
          ))}
          {nonAffectes > 0 && (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-500 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  ?
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-gray-600 m-0">Non affectés</p>
                  <p className="text-[10px] text-gray-400 m-0">
                    {nonAffectes} Dossier{nonAffectes > 1 ? "s" : ""} IR
                  </p>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 italic m-0">À répartir dans le tableau</p>
            </div>
          )}
        </div>
      )}

      {/* Tableau */}
      <div
        className="mt-4 w-full overflow-x-auto"
        onWheel={(e) => {
          if (e.deltaY !== 0) {
            e.currentTarget.scrollLeft += e.deltaY;
            e.preventDefault();
          }
        }}
      >
        <table
          className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
          style={{ fontSize: "0.72rem" }}
        >
          <thead>
            <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Dossier</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Collaborateur</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Logiciel</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-left uppercase tracking-wide">Statut</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-center uppercase tracking-wide">Accord client</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-center uppercase tracking-wide">Validation</th>
              <th className="px-3 py-2.5 text-white/90 font-normal text-center uppercase tracking-wide">Échéance</th>
              <th className="px-3 py-2.5 text-white/90 font-normal"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredSuivi.map((annuaire) => {
              const ir = getIRForYear(annuaire);
              const meta = STATUT_META[ir.statuts] || STATUT_DEFAUT;
              const clientLabel =
                annuaire.clients
                  ?.map((c) => c.nom)
                  .filter(Boolean)
                  .join(", ") || "";
              const validated = ir.validation === true;
              const overdue = isOverdue(ir);
              return (
                <tr
                  key={annuaire.id}
                  data-client-id={annuaire.id}
                  onClick={() => {
                    setDetailContact(annuaire);
                    setShowDetailOverlay(true);
                  }}
                  className={`transition-colors cursor-pointer ${
                    contactBloque(annuaire) ? "ligne-bloquee " : ""
                  }${
                    animatingId === annuaire.id
                      ? "todo-row-validating"
                      : overdue
                      ? "hover:bg-red-50 border-l-4 border-red-400"
                      : meta.row
                  }`}
                >
                  <td className="px-3 py-2.5 font-medium text-slate-900 relative overflow-visible">
                    {animatingId === annuaire.id && <span className="todo-done-badge">✓ Finalisé !</span>}
                    <div className="flex items-center gap-1.5">
                      {overdue && (
                        <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                      )}
                      <div>
                        <div
                          className={`font-semibold ${
                            overdue ? "text-red-900" : "text-[#6e1a36]"
                          }`}
                        >
                          {annuaire.nom} {annuaire.prenoms}
                          {contactBloque(annuaire) && <BadgeBloque />}
                        </div>
                        {clientLabel && (
                          <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                            {clientLabel}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">
                    <CollabPicker
                      user={allUsers.find((u) => u.id === ir.userId) || null}
                      allUsers={allUsers}
                      isOpen={openCollabMenuId === annuaire.id}
                      onToggle={() => {
                        setOpenCollabMenuId((v) => (v === annuaire.id ? null : annuaire.id));
                        setCollabSearch("");
                      }}
                      onSelect={(userId) => handleQuickReassign(annuaire, userId)}
                      search={collabSearch}
                      onSearchChange={(e) => setCollabSearch(e.target.value)}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    {ir.logiciel ? (
                      <span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-medium">
                        {ir.logiciel}
                      </span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {ir.statuts ? (
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.pill}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                        {ir.statuts}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${
                        ACCORD_DOT[ir.accordClient] || "bg-slate-200"
                      }`}
                      title={ir.accordClient || "Non renseigné"}
                    />
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {validated ? (
                      ir.dateValidation ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-medium tabular-nums whitespace-nowrap">
                          <Check className="w-2.5 h-2.5 text-emerald-600" />
                          {formatDate(ir.dateValidation)}
                        </span>
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                      )
                    ) : (
                      <Circle className="w-4 h-4 text-slate-200 mx-auto" />
                    )}
                  </td>
                  <td
                    className={`px-3 py-2.5 text-center text-xs font-medium ${
                      overdue ? "text-red-500" : "text-slate-500"
                    }`}
                  >
                    {ir.dueDate ? formatDate(ir.dueDate) : "-"}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleOpenOverlay(annuaire)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded-md text-slate-600 hover:text-[#6e1a36] hover:bg-slate-100 transition-all bg-slate-50 border border-slate-200"
                    >
                      <ExternalLink className="w-3 h-3" />
                      <span>Ouvrir</span>
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredSuivi.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-8 text-center text-slate-400 text-sm"
                >
                  Aucun dossier trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Overlay détail contact annuaire */}
      {showDetailOverlay && detailContact && (
        <ContactDetailOverlay
          contact={detailContact}
          onClose={() => {
            setShowDetailOverlay(false);
            setDetailContact(null);
          }}
        />
      )}

      {/* Overlay édition IR */}
      {showOverlay && selectedClient && (
        <div className="overlayAC">
          <div
            className="inoverlayLead"
            style={{ maxWidth: 520, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-8 pt-6 pb-4 border-b mb-5 flex items-center justify-between">
              <div className="text-xl font-bold text-slate-900">
                Suivi IR —{" "}
                <span className="text-[#6e1a36]">
                  {selectedClient.nom} {selectedClient.prenoms}
                </span>
              </div>
              <button
                onClick={handleCloseOverlay}
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-8 pb-6 space-y-5">
              {/* Année */}
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold text-slate-700">Année :</span>
                <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-700 font-medium">
                  {selectedYear}
                </span>
              </div>

              {/* Collaborateur */}
              <div className="relative">
                <label className="text-sm font-semibold text-slate-700 block mb-1">
                  Collaborateur assigné
                </label>
                <input
                  type="text"
                  value={searchUser}
                  onChange={handleSearchUser}
                  placeholder="Rechercher un collaborateur…"
                  autoComplete="off"
                  className="w-full h-10 rounded-md bg-white px-3 text-sm border-2 border-slate-300 focus:border-[#6e1a36] outline-none transition-all"
                />
                {userSuggestions.length > 0 && (
                  <ul className="absolute left-0 top-full mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg z-20">
                    {userSuggestions.map((user) => (
                      <li
                        key={user.id}
                        onClick={() => selectUser(user)}
                        className="cursor-pointer px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors border-b last:border-b-0"
                      >
                        {user.lastName} {user.name}
                      </li>
                    ))}
                  </ul>
                )}
                {formIR.userId && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1 rounded-full text-sm">
                      {getUserNameById(formIR.userId)}
                      <button
                        type="button"
                        onClick={() => {
                          setFormIR((prev) => ({ ...prev, userId: null }));
                          setSearchUser("");
                        }}
                        className="text-emerald-600 hover:text-red-500 font-medium transition-colors"
                      >
                        ✕
                      </button>
                    </span>
                  </div>
                )}
              </div>

              {/* Logiciel */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">
                  Logiciel
                </label>
                <select
                  value={formIR.logiciel}
                  onChange={(e) =>
                    setFormIR((prev) => ({ ...prev, logiciel: e.target.value }))
                  }
                  className="w-full h-10 rounded-md bg-white px-3 text-sm border-2 border-slate-300 focus:border-[#6e1a36] outline-none transition-all"
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="Manuel">Manuel</option>
                  <option value="Clickimpôt">Clickimpôt</option>
                  <option value="RCA">RCA</option>
                </select>
              </div>

              {/* Statut */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">
                  Statut
                </label>
                <select
                  value={formIR.statuts}
                  onChange={(e) =>
                    setFormIR((prev) => ({ ...prev, statuts: e.target.value }))
                  }
                  className="w-full h-10 rounded-md bg-white px-3 text-sm border-2 border-slate-300 focus:border-[#6e1a36] outline-none transition-all"
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="En cours">En cours</option>
                  <option value="À faire">À faire</option>
                  <option value="Finalisé">Finalisé</option>
                  <option value="En attente client">En attente client</option>
                </select>
              </div>

              {/* Accord client */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">
                  Accord client
                </label>
                <select
                  value={formIR.accordClient}
                  onChange={(e) =>
                    setFormIR((prev) => ({
                      ...prev,
                      accordClient: e.target.value,
                    }))
                  }
                  className="w-full h-10 rounded-md bg-white px-3 text-sm border-2 border-slate-300 focus:border-[#6e1a36] outline-none transition-all"
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="Oui">Oui</option>
                  <option value="Non">Non</option>
                  <option value="En attente">En attente</option>
                </select>
              </div>

              {/* Date d'échéance */}
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">
                  Date d'échéance
                </label>
                <input
                  type="date"
                  value={formIR.dueDate ? formIR.dueDate.split("T")[0] : ""}
                  onChange={(e) =>
                    setFormIR((prev) => ({
                      ...prev,
                      dueDate: e.target.value
                        ? `${e.target.value}T00:00:00`
                        : null,
                    }))
                  }
                  className="w-full h-10 rounded-md bg-white px-3 text-sm border-2 border-slate-300 focus:border-[#6e1a36] outline-none transition-all"
                />
              </div>

              {/* Validation */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="validation-ir"
                  checked={formIR.validation}
                  onChange={(e) =>
                    setFormIR((prev) => ({
                      ...prev,
                      validation: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 accent-[#6e1a36]"
                />
                <label
                  htmlFor="validation-ir"
                  className="text-sm font-semibold text-slate-700 cursor-pointer"
                >
                  Déclaration validée
                </label>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <button
                  type="button"
                  onClick={handleCloseOverlay}
                  className="px-6 h-10 border-2 border-slate-300 text-slate-700 rounded-md font-medium text-sm hover:bg-slate-100 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`flex items-center gap-2 px-6 h-10 rounded-md font-medium text-sm text-white transition-colors ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#6e1a36] hover:bg-[#5a1429]"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Enregistrer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuiviIR;
